import type maplibregl from 'maplibre-gl';

import {
    LEAK_ENTITY_STATUSES,
    POI_ENTITY_STATUSES,
    type EntityType,
    type LeakEntityStatus,
    type PoiEntityStatus,
} from './entityStatus';

// Entity-partitioned cluster accumulators for the split POI/leak sources. The shared
// clustered source holds both families, so the cluster card needs per-family counts and
// per-status rows; the bubble keeps showing point_count (= visible entities, because
// filtering is source-level).
//
// The counter set is the registries' full superset, disabled statuses included: MapLibre
// accepts clusterProperties only in the addSource spec (immutable afterwards), so a
// later status activation must not require a source rebuild. Disabled statuses simply
// accumulate zero until the server starts emitting them; zero rows are never displayed.

type ClusterProperties = NonNullable<maplibregl.GeoJSONSourceSpecification['clusterProperties']>;

export interface EntityClusterCounterDef {
    /** Cluster property name the accumulator is stored under (also the React row key). */
    property: string;
    entityType: EntityType;
    status: PoiEntityStatus | LeakEntityStatus;
    label: string;
}

// The wire tolerance rule, in expression form: a feature without entity_type is a POI.
const IS_LEAK: unknown = ['==', ['get', 'entity_type'], 'leak'];
const IS_POI: unknown = ['!=', ['get', 'entity_type'], 'leak'];

export const ENTITY_CLUSTER_COUNTERS: readonly EntityClusterCounterDef[] = [
    ...POI_ENTITY_STATUSES.map((d) => ({
        property: `count_poi_${d.status}`,
        entityType: 'poi' as const,
        status: d.status,
        label: d.label,
    })),
    ...LEAK_ENTITY_STATUSES.map((d) => ({
        property: `count_leak_${d.status}`,
        entityType: 'leak' as const,
        status: d.status,
        label: d.label,
    })),
];

/**
 * Builds the `clusterProperties` spec for the entity sources: the two family totals plus
 * one counter per registry status (superset — see the module note).
 */
export function buildEntityClusterProperties(): ClusterProperties {
    const properties: Record<string, unknown> = {
        count_pois: ['+', ['case', IS_POI, 1, 0]],
        count_leaks: ['+', ['case', IS_LEAK, 1, 0]],
    };
    for (const def of ENTITY_CLUSTER_COUNTERS) {
        const family = def.entityType === 'leak' ? IS_LEAK : IS_POI;
        properties[def.property] = ['+', ['case', ['all', family, ['==', ['get', 'status'], def.status]], 1, 0]];
    }
    return properties;
}

/**
 * The totals a Network Explorer cell carries alongside the per-status counters. A cell is
 * a server-side bin of many entities, so these arrive pre-counted; `count_total` is what
 * the bubble shows (never `point_count`, which would count cells).
 */
export const AGGREGATE_CELL_TOTAL_KEYS = ['count_total', 'count_pois', 'count_leaks'] as const;

/**
 * Builds the `clusterProperties` spec for the Network Explorer cells source. The cells
 * already carry every counter the entity sources accumulate per feature, so each
 * accumulator simply sums the cell's own value of the same name — which keeps
 * {@link readEntityClusterSummary} and the cluster card unchanged between the two models.
 * A counter the server does not emit (a disabled registry status) sums as zero.
 */
export function buildAggregateClusterProperties(): ClusterProperties {
    const properties: Record<string, unknown> = {};
    for (const key of AGGREGATE_CELL_TOTAL_KEYS) {
        properties[key] = ['+', ['coalesce', ['get', key], 0]];
    }
    for (const def of ENTITY_CLUSTER_COUNTERS) {
        properties[def.property] = ['+', ['coalesce', ['get', def.property], 0]];
    }
    return properties;
}

export interface EntityClusterBreakdownRow {
    key: string;
    entityType: EntityType;
    status: PoiEntityStatus | LeakEntityStatus;
    label: string;
    count: number;
}

export interface EntityClusterSummary {
    /** The cluster's point_count — what the bubble displays (= visible entities). */
    total: number;
    poiCount: number;
    leakCount: number;
    /** Non-zero POI rows in registry order. */
    poiRows: EntityClusterBreakdownRow[];
    /** Non-zero leak rows in registry order, plus a residual 'other' row if the counters ever under-account. */
    leakRows: EntityClusterBreakdownRow[];
}

function readCount(props: Record<string, unknown>, property: string): number {
    const value = props[property];
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/**
 * Reads a cluster feature's properties into the entity-sectioned breakdown for the hover
 * card. Zero rows are omitted; if the status counters under-account their family total
 * (a status shipped backend-first), the difference is appended as an Other row so each
 * section always sums to its family count.
 */
export function readEntityClusterSummary(props: Record<string, unknown>): EntityClusterSummary {
    const total = readCount(props, 'point_count');
    const poiCount = readCount(props, 'count_pois');
    const leakCount = readCount(props, 'count_leaks');

    const rowsFor = (entityType: EntityType, familyTotal: number): EntityClusterBreakdownRow[] => {
        const rows: EntityClusterBreakdownRow[] = [];
        for (const def of ENTITY_CLUSTER_COUNTERS) {
            if (def.entityType !== entityType) continue;
            const count = readCount(props, def.property);
            if (count <= 0) continue;
            rows.push({ key: def.property, entityType, status: def.status, label: def.label, count });
        }
        const counted = rows.reduce((sum, row) => sum + row.count, 0);
        if (familyTotal > counted) {
            rows.push({
                key: `other_${entityType}`,
                entityType,
                status: 'unclassified',
                label: 'Other',
                count: familyTotal - counted,
            });
        }
        return rows;
    };

    return {
        total,
        poiCount,
        leakCount,
        poiRows: rowsFor('poi', poiCount),
        leakRows: rowsFor('leak', leakCount),
    };
}

/**
 * The breakdown for a single entity drawn as a "1" bubble (a record the cluster radius left
 * alone). An unclustered feature carries no accumulators, only its own `entity_type` and
 * `status`, so the card is built from those: one family count of one and one row, falling
 * back to an Other row for a status the registry does not know. A feature without an
 * entity_type is a POI, the wire tolerance rule.
 */
export function readSingleEntitySummary(props: Record<string, unknown>): EntityClusterSummary {
    const entityType: EntityType = props['entity_type'] === 'leak' ? 'leak' : 'poi';
    const status = props['status'];
    const def = ENTITY_CLUSTER_COUNTERS.find((d) => d.entityType === entityType && d.status === status);
    const row: EntityClusterBreakdownRow = def
        ? { key: def.property, entityType, status: def.status, label: def.label, count: 1 }
        : { key: `other_${entityType}`, entityType, status: 'unclassified', label: 'Other', count: 1 };
    return {
        total: 1,
        poiCount: entityType === 'poi' ? 1 : 0,
        leakCount: entityType === 'leak' ? 1 : 0,
        poiRows: entityType === 'poi' ? [row] : [],
        leakRows: entityType === 'leak' ? [row] : [],
    };
}

/**
 * The same breakdown for a Network Explorer cell or a cluster of cells. Every feature on
 * that source is already an aggregate, so the headline is the sum of the two family counts —
 * `point_count` exists only on merged clusters and would count cells, not entities.
 */
export function readAggregateClusterSummary(props: Record<string, unknown>): EntityClusterSummary {
    const summary = readEntityClusterSummary(props);
    return { ...summary, total: summary.poiCount + summary.leakCount };
}
